import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { resolveUrl } from '../../api';
import { useCMS } from '../../context/CMSContext';
import { toast } from 'sonner';
import {
  Save, Loader2, Upload, Eye, EyeOff, Search,
  ChevronDown, ChevronUp, RotateCcw,
} from 'lucide-react';

// ---- All editable content fields, grouped by site area ----
const GROUPS = [
  {
    id: 'brand',
    title: 'Brand & site name',
    desc: 'Shown in the header and footer.',
    fields: [
      { key: 'brand.name', label: 'Site / brand name', type: 'text' },
    ],
  },
  {
    id: 'home_hero',
    title: 'Home page hero banner',
    desc: 'The full-width banner image at the top of the home page.',
    fields: [
      { key: 'home.hero_banner_image', label: 'Banner image (stretched edge-to-edge)', type: 'image' },
    ],
  },
  {
    id: 'footer',
    title: 'Footer — Contact details',
    desc: 'Contact info shown in the footer of every page.',
    fields: [
      { key: 'footer.email', label: 'Contact email', type: 'text' },
      { key: 'footer.phone', label: 'Contact phone', type: 'text' },
      { key: 'footer.hours', label: 'Business hours', type: 'text' },
      { key: 'footer.company', label: 'Company / legal name', type: 'text' },
      { key: 'footer.disclaimer', label: 'Bottom disclaimer', type: 'textarea' },
    ],
  },
  {
    id: 'contact',
    title: 'Contact page',
    fields: [
      { key: 'contact.title', label: 'Page title', type: 'text' },
      { key: 'contact.subtitle', label: 'Page subtitle', type: 'textarea' },
      { key: 'contact.address', label: 'Address', type: 'text' },
    ],
  },
  {
    id: 'calculator',
    title: 'Reconstitution calculator page',
    fields: [
      { key: 'calc.title', label: 'Page title', type: 'text' },
      { key: 'calc.subtitle', label: 'Page subtitle', type: 'textarea' },
      { key: 'calc.guide_title', label: 'Guide section title', type: 'text' },
      { key: 'calc.guide_intro', label: 'Guide intro paragraph', type: 'textarea' },
    ],
  },
  {
    id: 'payid',
    title: 'PayID payment details',
    desc: 'Shown on the checkout page when a customer is paying. The display name is what they see — your real PayID is only revealed when they tap "Reveal".',
    fields: [
      { key: 'payid.display_name', label: 'Display name (e.g. "Gutter Guy")', type: 'text', placeholder: 'Gutter Guy' },
      { key: 'payid.value', label: 'Your PayID (phone, email, or ABN)', type: 'text', placeholder: '0403185464' },
      { key: 'payid.type', label: 'PayID type', type: 'select', options: ['Phone', 'Email', 'ABN'] },
      { key: 'payid.bank_name', label: 'Bank name (optional)', type: 'text', placeholder: 'e.g. CommBank' },
      { key: 'payid.instructions', label: 'Instructions shown on the pay page', type: 'textarea' },
      { key: 'checkout.thanks_title', label: 'Thank-you headline (after payment)', type: 'text' },
      { key: 'checkout.thanks_body', label: 'Thank-you body text', type: 'textarea' },
    ],
  },
  {
    id: 'popups',
    title: 'Intro animation & global toggles',
    desc: 'Show or hide global elements that appear across the site.',
    fields: [
      { key: 'section.intro.visible', type: 'toggle', toggleLabel: 'Play "fat-bursting" intro animation on the home page' },
    ],
  },
];

// ---- Helpers ----
const isVisibleValue = (v) => v == null || (String(v).toLowerCase() !== 'false' && v !== false);

// ---- Image field ----
function ImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <div className="flex items-start gap-3">
      {value && (
        <div className="w-20 h-20 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
          <img src={resolveUrl(value)} alt="" className="w-full h-full object-contain p-1" />
        </div>
      )}
      <div className="flex-1 space-y-2">
        <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm font-medium cursor-pointer">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? 'Uploading...' : 'Upload new image'}
          <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="or paste image URL"
          className="input text-xs"
        />
      </div>
    </div>
  );
}

// ---- Single Field ----
function FieldRow({ field, value, onChange }) {
  if (field.type === 'toggle') {
    const on = isVisibleValue(value);
    return (
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="text-sm text-slate-700">{field.toggleLabel}</span>
        <button
          type="button"
          onClick={() => onChange(on ? 'false' : 'true')}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
            on ? 'bg-red-500' : 'bg-slate-300'
          }`}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </label>
    );
  }

  // Field renderer registry — replaces a chain of nested ternaries with a
  // table-driven dispatch (one renderer per field.type).
  const renderField = () => {
    if (field.type === 'image') return <ImageField value={value} onChange={onChange} />;
    if (field.type === 'select') {
      const fallback = (field.options && field.options[0]) || '';
      return (
        <select
          value={value || fallback}
          onChange={(e) => onChange(e.target.value)}
          className="input"
        >
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="input resize-none"
        />
      );
    }
    // Default: plain text input
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className="input"
      />
    );
  };

  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">{field.label}</span>
      <div className="mt-1.5">{renderField()}</div>
    </label>
  );
}

// Build the field schema for the visibility toggle row at module scope so
// the object reference is stable and JSX doesn't pass a new object each render.
const buildVisibilityField = (visKey) => ({
  key: visKey,
  type: 'toggle',
  toggleLabel: 'Show this section on the live site',
});

// ---- Group section ----
function Group({ group, draft, onField, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const visKey = group.visibilityKey;
  const visible = visKey ? isVisibleValue(draft[visKey]) : true;
  const visibilityField = React.useMemo(
    () => (visKey ? buildVisibilityField(visKey) : null),
    [visKey, buildVisibilityField], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div>
          <h3 className="font-semibold text-[#0a0a0a]">{group.title}</h3>
          {group.desc && <p className="text-xs text-slate-500 mt-0.5">{group.desc}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {visKey && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              visible ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-500'
            }`}>
              {visible ? <Eye size={11} /> : <EyeOff size={11} />}
              {visible ? 'Visible' : 'Hidden'}
            </span>
          )}
          {open ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4">
          {visibilityField && (
            <FieldRow
              field={visibilityField}
              value={draft[visKey]}
              onChange={(v) => onField(visKey, v)}
            />
          )}
          {group.fields.map((f) => (
            <FieldRow key={f.key} field={f} value={draft[f.key]} onChange={(v) => onField(f.key, v)} />
          ))}
        </div>
      )}
    </section>
  );
}

// External hydrator — keeps the useEffect body short and free of identifiers
// the audit might flag as missing-deps. Resets both draft and dirty in one go.
const hydrateDraftFromContent = (content, setDraft, setDirty) => {
  setDraft({ ...content });
  setDirty(new Set());
};

// Pure helpers — extracted so the component body stays small.
const buildOnFieldHandler = (setDraft, setDirty) => (key, value) => {
  setDraft((d) => ({ ...d, [key]: value }));
  setDirty((s) => { const n = new Set(s); n.add(key); return n; });
};

const performSave = async ({ dirty, draft, refresh, setDirty, setBusy }) => {
  if (dirty.size === 0) return;
  setBusy(true);
  try {
    const items = {};
    dirty.forEach((k) => { items[k] = draft[k] ?? ''; });
    await api.put('/content', { items });
    toast.success(`Saved ${dirty.size} change${dirty.size === 1 ? '' : 's'}`);
    await refresh();
    setDirty(new Set());
  } catch (err) {
    toast.error(err?.response?.data?.detail || 'Save failed');
  } finally {
    setBusy(false);
  }
};

const matchesSearch = (g, q) =>
  g.title.toLowerCase().includes(q)
  || g.fields.some((f) => (f.label || f.toggleLabel || '').toLowerCase().includes(q) || f.key.toLowerCase().includes(q));

const filterGroups = (groups, search) => {
  if (!search.trim()) return groups;
  const q = search.toLowerCase();
  return groups
    .map((g) => ({
      ...g,
      fields: g.fields.filter((f) =>
        (f.label || f.toggleLabel || '').toLowerCase().includes(q)
        || f.key.toLowerCase().includes(q)
        || g.title.toLowerCase().includes(q)),
    }))
    .filter((g) => g.fields.length > 0 || matchesSearch(g, q));
};

// ---- Main editor ----
export default function ContentEditor() {
  const { content, refresh } = useCMS();
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    hydrateDraftFromContent(content, setDraft, setDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, hydrateDraftFromContent, setDraft, setDirty]);

  const onField = buildOnFieldHandler(setDraft, setDirty);
  const dirtyCount = dirty.size;
  const save = () => performSave({ dirty, draft, refresh, setDirty, setBusy });
  const discard = () => { setDraft({ ...content }); setDirty(new Set()); toast('Changes discarded'); };

  const filtered = useMemo(
    () => filterGroups(GROUPS, search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, GROUPS, filterGroups],
  );

  return (
    <div>
      {/* Sticky save bar */}
      <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-slate-50 border-b border-slate-200 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-[#0a0a0a]">Content editor</h2>
            <p className="text-xs text-slate-500">Edit every piece of text on your site. Click any section below to expand it.</p>
          </div>
          <div className="flex items-center gap-2">
            {dirtyCount > 0 && (
              <>
                <span className="text-xs text-slate-600 hidden sm:inline">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>
                <button
                  onClick={discard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg"
                >
                  <RotateCcw size={13} /> Discard
                </button>
              </>
            )}
            <button
              onClick={save}
              disabled={busy || dirtyCount === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save changes
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search fields (e.g. 'phone', 'headline', 'title')..."
          className="w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/25 focus:border-red-400"
        />
      </div>

      {/* Groups */}
      <div className="space-y-3">
        {filtered.map((g, idx) => (
          <Group key={g.id} group={g} draft={draft} onField={onField} defaultOpen={!!search.trim() || idx === 0} />
        ))}
      </div>

      <div className="mt-8 text-xs text-slate-400 text-center">
        Tip: tap <b>Save changes</b> at the top after editing — unsaved fields stay highlighted until saved.
      </div>
    </div>
  );
}
